import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Tabs, Tab, Grid, Stack,
  FormControl, InputLabel, Select, MenuItem, TextField, Alert,
  CircularProgress, Chip, Snackbar,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import ManualEditor from '../components/Manual/ManualEditor';
import ManualChapterView from '../components/Manual/ManualChapterView';
import ManualTableOfContents from '../components/Manual/ManualTableOfContents';
import useDocumentEditorStore from '../hooks/useDocumentEditorStore';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';
import useManualPalette from '../hooks/useManualPalette';
import { ManualPreviewProvider } from '../contexts/ManualPreviewContext';
import { getEditorInputSx } from '../components/Manual/manualPalette';
import { alpha } from '@mui/material/styles';

const VAULT_OPTIONS = ['procedures', 'legal', 'technical', 'financial', 'marketing', 'contracts', 'customer', 'hr', 'compliance'];
const CLASSIFICATION_OPTIONS = ['unclassified', 'internal', 'confidential'];

export default function DocumentEditorPage() {
  const DmsMP = useDmsPalette();
  const MP = useManualPalette();
  const { documentId: routeDocId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [documentId, setDocumentId] = useState(routeDocId || null);
  const [loading, setLoading] = useState(!!routeDocId);
  const [activeTab, setActiveTab] = useState(0);
  const [activeChapter, setActiveChapter] = useState('ch1');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [docStatus, setDocStatus] = useState('draft');
  const [snack, setSnack] = useState('');

  const [docSettings, setDocSettings] = useState(() => {
    const boot = location.state?.editorBootstrap;
    return boot?.docSettings || {
      vault: 'procedures',
      classification: 'internal',
      description: '',
    };
  });

  useEffect(() => {
    if (location.state?.editorBootstrap && !routeDocId) {
      navigate('/editor', { replace: true, state: null });
    }
  }, [location.state, routeDocId, navigate]);

  const [initialPayload, setInitialPayload] = useState(() => {
    if (routeDocId) return undefined;
    const boot = location.state?.editorBootstrap;
    return boot?.content ? JSON.stringify(boot.content) : null;
  });
  const store = useDocumentEditorStore(initialPayload);
  const { content, imageOverrides, actions, getSavePayload } = store;

  useEffect(() => {
    if (!routeDocId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const doc = await dmsApi.getDocument(routeDocId);
        if (cancelled) return;
        setDocStatus(doc.status || 'draft');
        setDocSettings({
          vault: doc.vault || 'procedures',
          classification: doc.classification || 'internal',
          description: doc.description || '',
        });
        setInitialPayload(doc.extractedText || null);
      } catch (err) {
        if (!cancelled) setError(err.error || 'Failed to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [routeDocId]);

  useEffect(() => {
    if (activeTab !== 1) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveChapter(e.target.id.replace('chapter-', ''));
        });
      },
      { threshold: 0.15 },
    );
    document.querySelectorAll('[id^="chapter-"]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeTab, content.chapters]);

  const readOnly = docStatus === 'published' || docStatus === 'archived';

  useEffect(() => {
    if (readOnly) setActiveTab(1);
  }, [readOnly]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = getSavePayload();
      const res = await dmsApi.saveEditorDocument({
        content: payload,
        title: content.meta.title,
        vault: docSettings.vault,
        classification: docSettings.classification,
        description: docSettings.description,
        documentId: documentId || undefined,
      });
      const doc = res.document;
      if (doc?._id && !documentId) {
        setDocumentId(doc._id);
        navigate(`/editor/${doc._id}`, { replace: true });
      }
      setDocStatus(doc?.status || 'draft');
      setSnack('Document saved as draft');
    } catch (err) {
      setError(err.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [content.meta.title, docSettings, documentId, getSavePayload, navigate]);

  const handleSubmit = async () => {
    if (!documentId) {
      setError('Save the document first before submitting for approval');
      return;
    }
    setSubmitting(true);
    try {
      await dmsApi.initiateWorkflow(documentId, { type: 'publish_approval' });
      setDocStatus('review');
      setSnack('Submitted for approval');
    } catch (err) {
      setError(err.error || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || (routeDocId && initialPayload === undefined && !error)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', mx: { xs: -1.5, sm: -2, md: -3 }, mt: { xs: -1.5, sm: -2, md: -3 } }}>
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          borderBottom: `1px solid ${DmsMP.border}`,
          bgcolor: DmsMP.deepBlue,
        }}
      >
        <DescriptionIcon sx={{ color: DmsMP.blue }} />
        <Typography variant="h6" fontWeight={700} sx={{ color: DmsMP.white }}>
          Document Editor
        </Typography>
        {docStatus && (
          <Chip label={docStatus} size="small" sx={{ textTransform: 'capitalize' }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: 40 }}>
          <Tab icon={<EditIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Edit" sx={{ minHeight: 40, textTransform: 'none' }} />
          <Tab icon={<VisibilityIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Preview" sx={{ minHeight: 40, textTransform: 'none' }} />
        </Tabs>
        <Stack direction="row" spacing={1}>
          {documentId && (
            <Button size="small" variant="text" onClick={() => navigate(`/document/${documentId}`)}>
              View in DMS
            </Button>
          )}
          {!readOnly && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ borderColor: MP.green, color: MP.green }}
              >
                Save Draft
              </Button>
              {documentId && docStatus === 'draft' && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  Submit for Approval
                </Button>
              )}
            </>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 3, mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {readOnly && (
        <Alert severity="info" sx={{ mx: 3, mt: 2 }}>
          This document is {docStatus} and cannot be edited. View only mode.
        </Alert>
      )}

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {!readOnly && activeTab === 0 && (
          <PaperSettings
            docSettings={docSettings}
            onChange={setDocSettings}
            disabled={!!documentId && docStatus === 'review'}
            MP={MP}
          />
        )}

        {activeTab === 0 && !readOnly ? (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={actions.addChapter}>
                Add Chapter
              </Button>
            </Box>
            <ManualEditor content={content} imageOverrides={imageOverrides} actions={actions} />
          </Box>
        ) : activeTab === 1 || readOnly ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
              <ManualTableOfContents chapters={content.chapters} activeChapter={activeChapter} />
            </Grid>
            <Grid item xs={12} md={9}>
              <Box
                sx={{
                  p: 4,
                  mb: 4,
                  background: `linear-gradient(135deg, ${MP.black} 0%, ${MP.deepBlue} 60%, ${MP.blue} 100%)`,
                  borderRadius: 3,
                  borderLeft: `6px solid ${MP.yellow}`,
                }}
              >
                <Typography variant="h4" fontWeight={900} sx={{ color: MP.white }}>
                  {content.meta.title}
                </Typography>
                {content.meta.subtitle && (
                  <Typography sx={{ color: MP.textMuted, mt: 1 }}>{content.meta.subtitle}</Typography>
                )}
              </Box>
              <ManualPreviewProvider>
                <Box
                  sx={{
                    bgcolor: '#ffffff',
                    color: '#263238',
                    borderRadius: 3,
                    p: { xs: 2, md: 4 },
                    border: `1px solid ${alpha('#1a237e', 0.1)}`,
                    boxShadow: `0 2px 12px ${alpha('#000', 0.08)}`,
                  }}
                >
                  {content.chapters.map((ch, idx) => (
                    <ManualChapterView
                      key={ch.id}
                      chapter={ch}
                      chapterIndex={idx}
                      imageOverrides={imageOverrides}
                    />
                  ))}
                </Box>
              </ManualPreviewProvider>
            </Grid>
          </Grid>
        ) : null}
      </Container>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function PaperSettings({ docSettings, onChange, disabled, MP }) {
  const settingsSx = getEditorInputSx(MP);
  return (
    <Box
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${MP.border}`,
        bgcolor: MP.cardBg,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 2fr' },
        gap: 2,
      }}
    >
      <FormControl size="small" fullWidth disabled={disabled} sx={settingsSx}>
        <InputLabel>Vault</InputLabel>
        <Select
          value={docSettings.vault}
          label="Vault"
          onChange={(e) => onChange((p) => ({ ...p, vault: e.target.value }))}
        >
          {VAULT_OPTIONS.map((v) => (
            <MenuItem key={v} value={v} sx={{ textTransform: 'capitalize' }}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" fullWidth disabled={disabled} sx={settingsSx}>
        <InputLabel>Classification</InputLabel>
        <Select
          value={docSettings.classification}
          label="Classification"
          onChange={(e) => onChange((p) => ({ ...p, classification: e.target.value }))}
        >
          {CLASSIFICATION_OPTIONS.map((c) => (
            <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        fullWidth
        label="Description"
        value={docSettings.description}
        onChange={(e) => onChange((p) => ({ ...p, description: e.target.value }))}
        disabled={disabled}
        sx={settingsSx}
      />
    </Box>
  );
}